import {
  Colors,
  Flex,
  IconChevronDown,
  IconChevronUp,
  RemoteImage,
  Spinner,
  Tag,
  Typography,
} from '@banhala/ui-kit';
import type {
  GoodsOptionsBriefResponse,
  OptionComponent,
  OptionGroup,
} from 'shared/src/api';
import React, { useCallback, useMemo } from 'react';
import { useGoodsOptions } from '../../queries/useGoodsOptions';
import Animated from 'react-native-reanimated';
import { bridge, isNotNil, localizeNumber } from 'shared/src/utils';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { useGetOrderDeadlineText } from '../../hooks/useGetOrderDeadlineText';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { usePostRestockNotification } from '../../queries/usePostRestockNotification';
import { systemAlert } from 'shared/src/component';
import { useLogViewLastDepthGoodsOptionModal } from '../../hooks/useLogViewLastDepthGoodsOptionModal';
import { useIsAnonymousUser } from 'shared/src/hooks';
import { OptionBadge } from './OptionBadge';

const OptionItemView: React.FC<{
  option: OptionComponent;
  index: number;
  prevSelectedOptionSno: number | undefined;
  onPress: () => void;
  optionsBrief?: GoodsOptionsBriefResponse;
  showDeliveryType: boolean;
  onPressApplyForReStock: () => void;
}> = props => {
  const isAnonymousUser = useIsAnonymousUser();

  const { option } = props;

  const isGiftMode = useIsBottomSheetGiftMode();
  const {
    mutateAsync: postRestockNotification,
    isPending: loadingPostRestockNotification,
  } = usePostRestockNotification(option.goods_option_sno, {
    depth: option.depth,
    selected_option_sno: props.prevSelectedOptionSno,
  });

  const isPreOrder = props.optionsBrief?.sales_method === 'PRE_ORDER';

  const isTodayDelivery = useMemo(() => {
    if (isGiftMode) return false;

    return option.delivery_type === 'today' || option.delivery_type === 'shak';
  }, [option.delivery_type, isGiftMode]);

  const isThirdPLDelivery = useMemo(() => {
    if (isGiftMode) return false;

    return option.delivery_type === 'third_pl';
  }, [option.delivery_type, isGiftMode]);

  const isSoldOut = useMemo(
    () => props.option.goods_option?.is_soldout ?? false,
    [props.option.goods_option],
  );
  const getDeadlineText = useGetOrderDeadlineText();

  const orderDeadline = useMemo(() => {
    if (isPreOrder) return null;
    if (!isTodayDelivery && !isThirdPLDelivery) return null;
    if (isSoldOut) {
      return '품절';
    }
    return getDeadlineText({ delivery_type: option.delivery_type });
  }, [
    getDeadlineText,
    isPreOrder,
    isSoldOut,
    isTodayDelivery,
    isThirdPLDelivery,
    option.delivery_type,
  ]);

  const onPressRow = useCallback(() => {
    if (isSoldOut) return;

    const maxOrderLimit = option.goods_option?.max_order_limit_count;
    const purchaseCount = option.goods_option?.purchase_count ?? 0;
    if (isNotNil(maxOrderLimit) && maxOrderLimit <= purchaseCount) {
      systemAlert(
        '이미 최대 수량까지 구매했어요',
        `${option.name} 옵션은 인당 최대 ${maxOrderLimit}개까지 구매할 수 있어요.`,
        [{ text: '확인' }],
      );
      return;
    }

    props.onPress();
  }, [isSoldOut, option, props]);

  const checkRestockNoti = useCallback(() => {
    return new Promise<boolean>(resolve => {
      systemAlert(
        '상품이 재입고 되면 푸시 알림으로 알려드릴게요. 재입고 알림을 받을까요?',
        undefined,
        [
          { text: '취소', onPress: () => resolve(false) },
          { text: '확인', onPress: () => resolve(true) },
        ],
      );
    });
  }, []);

  const onPressApplyForReStock = useCallback(async () => {
    if (!isSoldOut) return;
    if (!option.goods_option?.is_possible_applying_restock_noti) return;

    if (option.goods_option.is_applied_for_restock) return;

    if (isAnonymousUser) {
      bridge.assertLogin({});
      return;
    }

    //재입고 알림 받기 update
    const alertResult = await checkRestockNoti();
    if (!alertResult) return;

    try {
      await postRestockNotification();
      bridge.toast('재입고 알림 신청이 완료되었습니다.');
    } catch (ex) {
      const error = ex as Error;
      bridge.toast(error.message);
    }
  }, [
    checkRestockNoti,
    isSoldOut,
    isAnonymousUser,
    option.goods_option,
    postRestockNotification,
  ]);

  return (
    <Flex.Row
      onPress={onPressRow}
      borderTopWidth={1}
      testID={`옵션선택?LIST_INDEX=${props.index}`}
      borderColor={'border_tertiary'}
      padding={{ horizontal: 16, vertical: 16 }}
    >
      {isNotNil(option.image_url) && (
        <Flex
          width={38}
          height={38}
          borderRadius={'small'}
          overflow={'hidden'}
          margin={{ right: 12 }}
        >
          <RemoteImage uri={option.image_url} width={38} height={38} />
        </Flex>
      )}
      {isNotNil(option.color_code) && (
        <Flex borderRadius={'small'} overflow={'hidden'} margin={{ right: 12 }}>
          <Flex
            width={20}
            height={20}
            borderRadius={10}
            backgroundColorValue={option.color_code}
          />
        </Flex>
      )}
      <Flex flex={1} gap={4} justifyContent={'center'}>
        <Typography
          type={'subtitle2'}
          numberOfLines={1}
          color={isSoldOut ? 'content_disabled' : undefined}
        >
          {option.name}
        </Typography>
        {props.showDeliveryType && !isGiftMode && (
          <Flex>
            <OptionBadge
              deliveryType={option.delivery_type}
              isSoldOut={isSoldOut}
              orderDeadline={orderDeadline}
            />
          </Flex>
        )}

        {!props.showDeliveryType && option.goods_option?.is_soldout && (
          <Typography type={'body4'} color={'content_disabled'}>
            품절
          </Typography>
        )}
      </Flex>
      {isSoldOut && option.goods_option?.is_possible_applying_restock_noti ? (
        <Flex onPress={onPressApplyForReStock} justifyContent={'center'}>
          {loadingPostRestockNotification ? (
            <Spinner size={12} />
          ) : option.goods_option?.is_applied_for_restock ? (
            <Typography type={'body2'} color={'content_disabled'}>
              알림 신청 완료
            </Typography>
          ) : (
            <Typography type={'body2'} color={'content_blue_primary'}>
              재입고 알림 받기
            </Typography>
          )}
        </Flex>
      ) : (
        <Flex
          width={88}
          alignItems={'flex-end'}
          justifyContent={'center'}
          gap={4}
        >
          <Flex>
            {option.option_minimum_additional_price !== 0 && (
              <Typography color={'content_tertiary'} type={'body2'}>
                {option.option_minimum_additional_price > 0 ? '+' : ''}
                {localizeNumber(option.option_minimum_additional_price)}원
              </Typography>
            )}
          </Flex>
          <Flex.Row gap={4} alignItems={'center'}>
            {option.has_high_demand_tag && (
              <Tag size={'xsmall'} color={'pink'} type={'secondary'}>
                주문 많음
              </Tag>
            )}
            {(option.goods_option?.stock ?? 0) >= 1 &&
              (option.goods_option?.stock ?? 0) <= 9 && (
                <Typography color={'content_pink_primary'} type={'body4'}>
                  {option.goods_option?.stock}개 남음
                </Typography>
              )}
          </Flex.Row>
        </Flex>
      )}
    </Flex.Row>
  );
};

export const SelectOptionItemView: React.FC<{
  option: OptionGroup;
  index: number;
  onlyShowTodayDelivery: boolean;
  selectedOptions: { [key: number]: OptionComponent };
  onSelectOption: (option: OptionComponent) => void;
  onPressMenu: () => void;
  isOpen: boolean;
}> = props => {
  const { data: goodsOptionsBrief } = useGoodsOptionsBrief();

  const { data: goodsOptions, isLoading } = useGoodsOptions(
    props.option.depth > 1
      ? {
          depth: props.option.depth,
          selected_option_sno:
            props.selectedOptions[props.option.depth - 1]?.goods_option_sno ??
            undefined,
        }
      : {
          depth: 1,
        },
  );

  const isFinalDepth = useMemo(
    () =>
      (
        goodsOptions?.option_components.filter(
          option => option.is_final_depth,
        ) ?? []
      ).length > 0,
    [goodsOptions],
  );

  useLogViewLastDepthGoodsOptionModal({
    isFinalDepth,
    options: goodsOptions?.option_components ?? [],
  });

  return (
    <Flex
      borderRadius={'small'}
      borderWidth={1}
      borderColor={'border_secondary'}
    >
      <Flex.Row
        padding={{ horizontal: 12, vertical: 16 }}
        justifyContent={'space-between'}
        onPress={props.onPressMenu}
        testID={`옵션리스트?LIST_INDEX=${props.index}`}
      >
        {isNotNil(props.selectedOptions[props.option.depth]) ? (
          <Typography type={'subtitle2'} color={'content_primary'}>
            {props.selectedOptions[props.option.depth].name}
          </Typography>
        ) : (
          <Typography type={'body2'} color={'content_tertiary'}>
            {props.option.name} 선택하기
          </Typography>
        )}
        {props.isOpen ? (
          <IconChevronUp fill={Colors.content_tertiary} size={20} />
        ) : (
          <IconChevronDown fill={Colors.content_tertiary} size={20} />
        )}
      </Flex.Row>
      <Animated.View
        style={{ height: props.isOpen ? 'auto' : 0, overflow: 'hidden' }}
      >
        {isLoading && (
          <Flex.Center height={220}>
            <Spinner />
          </Flex.Center>
        )}
        {goodsOptions?.option_components
          .filter(item => {
            if (props.onlyShowTodayDelivery) {
              return (
                item.delivery_type === 'today' || item.delivery_type === 'shak'
              );
            }

            return true;
          })
          .map((option, index) => (
            <OptionItemView
              key={option.sno}
              option={option}
              index={index}
              prevSelectedOptionSno={
                props.selectedOptions[props.option.depth - 1]
                  ?.goods_option_sno ?? undefined
              }
              optionsBrief={goodsOptionsBrief}
              showDeliveryType={
                goodsOptionsBrief?.sales_method !== 'PRE_ORDER' &&
                goodsOptionsBrief?.goods_delivery_type !== 'standard'
              }
              onPress={() => {
                props.onSelectOption(option);
              }}
              onPressApplyForReStock={() => {}}
            />
          ))}
      </Animated.View>
    </Flex>
  );
};
